"use memo"

import { cleanFullAddress, PropertyAddress } from '@urbanx/agx-ui-components'
import {
  AutopilotTaskDto,
  TaskCategoryStage,
  TaskCategoryTaskType,
  TaskCategoryWorkflowType,
  useInvalidateUseGetAutopilotTasksForCampaign,
  useWithdrawWorkflow,
  WorkflowTaskRequest,
} from '@urbanx/agx.activities.client'
import { AuthContext } from '@urbanx/agx.auth.client'
import { MenuTitles } from 'constants/menu'
import { useSelectedAgency, useSelectedCampaign } from 'hooks'
import { ContentLayout, PageLayout } from 'layout'
import { useContext, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  AgxButton,
  AgxModal,
  AgxRow,
  Icon_AlertCircleOutline,
} from '@urbanx/agx.ui-components2'
import Tasks, { TaskTabs } from 'components/tasks/Tasks'
import AgentExchange from './ExchangeTasks/AgentExchange'
import ContractExchange from './ExchangeTasks/ContractExchange'
import SolicitorExchange from './ExchangeTasks/SolicitorExchange'

const exchangeTaskTypes = [
  TaskCategoryTaskType.SolicitorExchangeInformation,
  TaskCategoryTaskType.SolicitorExchangeContractDates,
  TaskCategoryTaskType.AgentExchangeInformation,
  TaskCategoryTaskType.AgentExchangeContractDates,
  TaskCategoryTaskType.ContractExchangeInformation,
  TaskCategoryTaskType.ContractExchangeContractDates,
] as const

const isExchangeTaskType = (taskType?: TaskCategoryTaskType) =>
  !!taskType &&
  exchangeTaskTypes.includes(taskType as (typeof exchangeTaskTypes)[number])


const hasTaskBlockers = (task: AutopilotTaskDto) =>
  (task.blockers?.length ?? 0) > 0

const isUnlockedUnblockedExchangeTask = (task: AutopilotTaskDto) =>
  task.isLocked === false &&
  isExchangeTaskType(task.category?.taskType) &&
  !hasTaskBlockers(task)

const ContractTasks = () => {
  const { contractId } = useParams()
  const { userAccount } = useContext(AuthContext)
  const { agency } = useSelectedAgency()
  const { campaign } = useSelectedCampaign()

  const [showTerminateConfirmation, setShowTerminateConfirmation] = useState(false)
  const [allTasks, setAllTasks] = useState<AutopilotTaskDto[]>([])

  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign()

  const { withdrawWorkflow } = useWithdrawWorkflow({
    onSuccess: () => invalidateTasks(campaign?.campaignId ?? '', contractId),
    onError: () => {},
  })

  const onTerminate = () => setShowTerminateConfirmation(true)

  const onConfirmTerminate = () => {
    const request: WorkflowTaskRequest = {
      campaignId: campaign?.campaignId,
      contractId,
    }
    withdrawWorkflow(request)
    setShowTerminateConfirmation(false)
  }

  const terminatedTask = allTasks.find(
    (t) => t.category?.taskType === TaskCategoryTaskType.Termination
  )

  const handleTasksChange = (tasks: AutopilotTaskDto[]) => {
    setAllTasks(tasks)
  }

  const exchangeTasks = allTasks.filter(isUnlockedUnblockedExchangeTask)

  const allExchangeTasksCompleted =
    exchangeTasks.length > 0 &&
    exchangeTasks.every((task) => task.isCompleted === true)

  const taskFilter = (task: AutopilotTaskDto) =>
    !isUnlockedUnblockedExchangeTask(task)

  const renderExchangeTask = (task: AutopilotTaskDto) => {
    if (!campaign?.campaignId) return null

    switch (task.category?.taskType) {
      case TaskCategoryTaskType.SolicitorExchangeInformation:
      case TaskCategoryTaskType.SolicitorExchangeContractDates:
        return (
          <SolicitorExchange
            key={`${task.contractId}-${task.category.taskType}`}
            campaignId={campaign.campaignId}
            task={task}
          />
        )
      case TaskCategoryTaskType.AgentExchangeInformation:
      case TaskCategoryTaskType.AgentExchangeContractDates:
        return (
          <AgentExchange
            key={`${task.contractId}-${task.category.taskType}`}
            campaignId={campaign.campaignId}
            task={task}
          />
        )
      case TaskCategoryTaskType.ContractExchangeInformation:
      case TaskCategoryTaskType.ContractExchangeContractDates:
        return (
          <ContractExchange
            key={`${task.contractId}-${task.category.taskType}`}
            campaignId={campaign.campaignId}
            task={task}
          />
        )
      default:
        return null
    }
  }

  const renderAdditionalContent = ({
    selectedTab,
    selectedWorkflowStage,
    allTasksCompleted,
    isViewingCompletedStage,
  }: {
    selectedTab: TaskTabs
    selectedWorkflowStage?: TaskCategoryStage
    allTasksCompleted: boolean
    isViewingCompletedStage: boolean
  }) => {
    if (selectedTab !== TaskTabs.Tasks) return null
    if (
      isViewingCompletedStage &&
      selectedWorkflowStage !== TaskCategoryStage.Exchange
    )
      return null
    if (allTasksCompleted && !isViewingCompletedStage) return null
    if (selectedWorkflowStage !== TaskCategoryStage.Exchange) return null
    if (exchangeTasks.length === 0) return null
    if (allExchangeTasksCompleted && !isViewingCompletedStage) return null

    return exchangeTasks.map(renderExchangeTask)
  }

  // Get unlocked contract tasks to determine if terminate button should be disabled
  const unlockedContractTasks = allTasks.filter(
    (t) =>
      !t.isLocked &&
      t.category?.workflowType === TaskCategoryWorkflowType.Contract
  )

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={agency?.name || ''}
      currentPageTitle={`Campaigns / ${cleanFullAddress(campaign?.address as PropertyAddress)}`}
      isCampaignsSection={true}
    >
      <ContentLayout
        hasSecondaryMenu={false}
        secondaryActiveMenu={MenuTitles.CONTRACT_TASKS}
        isContracts={true}
        secondaryActions={
          <AgxButton
            id={'btnTerminateContract'}
            text={'Terminate contract'}
            variant={'naked'}
            danger
            onClick={onTerminate}
            disabled={
              unlockedContractTasks.length === 0 ||
              (terminatedTask && !terminatedTask.isLocked)
            }
          />
        }
      >
        <Tasks
          campaignId={campaign?.campaignId ?? ''}
          contractId={contractId}
          primaryWorkflowType={TaskCategoryWorkflowType.Contract}
          taskFilter={taskFilter}
          onTasksChange={handleTasksChange}
          renderAdditionalContent={renderAdditionalContent}
        />

        <AgxModal
          title={'Mark as Terminated?'}
          titleIcon={
            <div className="modelTitleIconContainer">
              <Icon_AlertCircleOutline type={'Danger'} height={40} width={40} />
            </div>
          }
          type={'single'}
          variant={'withDivider'}
          actionButtonContainerStyle={'withDivider'}
          actionButtons={
            <AgxRow gap={'spacing-md'} justifyContent="center" fill>
              <AgxButton
                id={'btnCancel'}
                text={'Cancel'}
                variant={'hollow'}
                onClick={() => setShowTerminateConfirmation(false)}
                stretch
              />
              <AgxButton
                id={'btnConfirm'}
                text={'Confirm'}
                variant={'primary'}
                danger
                onClick={onConfirmTerminate}
                stretch
              />
            </AgxRow>
          }
          content={<></>}
          isOpen={showTerminateConfirmation}
          setIsDialogOpen={setShowTerminateConfirmation}
        />
      </ContentLayout>
    </PageLayout>
  )
}

export default ContractTasks
